import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useAtomValue } from 'jotai';

import type { GetTemplateCreationRecordResponse } from '../../../../contracts';
import { GET_TEMPLATE_CREATION_RECORD_BY_ID } from '../../../../contracts';
import { deleteTemplateVersionControl } from '../../../../data';
import { templateCreationRecordIdAtom } from '../../atoms';

interface DeleteVersionControlParams {
  versionControlId: string;
}

export const useMutateDeleteVersionControl = () => {
  const templateCreationRecordId = useAtomValue(templateCreationRecordIdAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ versionControlId }: DeleteVersionControlParams) => {
      await deleteTemplateVersionControl(versionControlId);
    },
    onMutate: async ({ versionControlId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<GetTemplateCreationRecordResponse>([
        GET_TEMPLATE_CREATION_RECORD_BY_ID,
        { templateCreationRecordId },
      ]);

      // Optimistically update the cache
      queryClient.setQueryData<GetTemplateCreationRecordResponse>(
        [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
        (oldData) => {
          if (!oldData) return oldData;

          return produce(oldData, (draft) => {
            draft.version_controls = draft.version_controls.filter(
              (vc) => vc.id !== versionControlId
            );
          });
        }
      );

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<GetTemplateCreationRecordResponse>(
          [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }],
          context.previousData
        );
      }
    },
  });
};
